import React, { useEffect } from 'react';
import './StudentDetails.css';
import { Box, Divider, IconButton, Menu, MenuItem, Tab, Tabs, Typography } from '@mui/material';
import AdminNavbar from '../../../../../components/Admin/AdminNavbar/AdminNavbar';
import Layout from '../../../../../components/Layout';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import { axiosInstance } from '../../../../../utils/tokenUtils';
import PropTypes from 'prop-types';
import StudentSession from '../../../../../components/Admin/UserManagement/Student/StudentSession/StudentSession';
import StudentWallet from '../../../../../components/Admin/UserManagement/Student/StudentWallet/StudentWallet';
import StudentOverview from '../../../../../components/Admin/UserManagement/Student/StudentOverview/StudentOverview';
import StudentEnableModal from '../../../../../components/Admin/UserManagement/Student/StudentEnableModal/StudentEnableModal';
import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import ActionAuditBanner from '../../../../../components/Admin/Common/ActionAuditBanner/ActionAuditBanner';
import { formatDateTime } from '../../../../../utils/dateUtils';
import {
  formatActorNameWithRole,
  getUserAccountAudit,
  getAccountStatus,
} from '../../../../../utils/auditActorUtils';
import { formatText } from '../../../../../utils/textUtils';

const StatusColor = {
  enabled: { color: '#14B334', bgcolor: '#F2FFF5', value: 'Enabled' },
  disabled: { color: '#949DA6', bgcolor: '#F5F5F5', value: 'Disabled' },
};

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const StudentDetails = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [rowId, setRowId] = useState(null);
  const [studentName, setStudentName] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [studentDetails, setStudentDetails] = useState([]);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Step back through history when we arrived from the learners list, so it
  // reopens on the page and filters it was left on. Falls back to the plain
  // list when this page was opened directly (no history to step back to).
  const handleBackToLearners = () => {
    if (location.key === 'default') {
      navigate('/admin/users/learner');
      return;
    }
    navigate(-1);
  };
  const [value, setValue] = useState(0);

  // RBAC permissions
  const [canManageUsers, setCanManageUsers] = useState(false);
  const [isSupportAssociate, setIsSupportAssociate] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axiosInstance.get('/admin/rbac/admin/me');
        const perms = Array.isArray(data?.permissions) ? data.permissions : [];
        const roles = Array.isArray(data?.roles) ? data.roles : [];
        if (mounted) {
          setCanManageUsers(perms.includes('manage_users'));
          setIsSupportAssociate(roles.includes('support_associate'));
        }
      } catch (_) {
        if (mounted) {
          setCanManageUsers(false);
          setIsSupportAssociate(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const getSingleStudentDetails = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`/admin/student/${id}`);
      setStudentDetails(data);
    } catch (error) {
      console.log(error);
    }
  }, [id]);

  useEffect(() => {
    getSingleStudentDetails();
  }, [getSingleStudentDetails, id]);

  const handleMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = action => {
    if (action === 'disable' || action === 'enable') {
      if (!canManageUsers) {
        toast.error("You don't have permission to enable/disable users");
        handleMenuClose();
        return;
      }
      setOpenApproveModal(true);
      setRowId(studentDetails?.id);
      setStudentName(`${studentDetails?.first_name} ${studentDetails?.last_name}`);
      // Treat a partially-blocked account as "not enabled" so the modal runs the
      // enable flow (which restores every feature) instead of the disable flow.
      setIsActive(getAccountStatus(studentDetails) === 'enabled');
    }
    handleMenuClose();
  };

  const handlePasswordReset = async () => {
    const email = studentDetails?.email;

    if (!email) {
      toast.error('Email address not found for this learner');
      handleMenuClose();
      return;
    }

    setIsResettingPassword(true);
    try {
      const response = await axiosInstance.post('/auth/forgot-password', {
        email: email,
      });

      if (response.status === 200) {
        toast.success(`Password reset email sent to ${email}`);
        handleMenuClose();
      }
    } catch (error) {
      console.error('Password reset error:', error);
      let errorMessage = 'Failed to send password reset email';

      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail[0]?.msg || errorMessage;
        } else {
          errorMessage = error.response.data.detail;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const { disabled: disabledAudit, enabled: enabledAudit } = getUserAccountAudit(studentDetails);
  const accountStatus = getAccountStatus(studentDetails);
  const statusStyle = StatusColor[accountStatus];

  return (
    <Layout title="Klass Online | Learner Details">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
          <Box className="flex flex-col p-[1rem] gap-3">
            <Box className="flex justify-between items-center">
              <Box className="flex flex-col">
                <Box className="flex items-center gap-2">
                  <Typography
                    className="user-management-request-text"
                    onClick={handleBackToLearners}
                  >
                    User Management {'>'}
                    {'>'} Learners {'>'}
                    {'>'}
                  </Typography>
                  <Typography className="profile-text">Profile</Typography>
                </Box>
                <Box className="flex items-center gap-4">
                  <Typography className="teacher-request-name-text">
                    {studentDetails?.first_name} {studentDetails?.last_name}
                  </Typography>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      height: '16px',
                      mt: 1,
                      borderColor: '#D5DEE3',
                    }}
                  />
                  <Box
                    sx={{
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '4px 8px 4px 8px',
                      borderRadius: '20px',
                      backgroundColor: statusStyle?.bgcolor,
                      color: statusStyle?.color,
                      border: `1px solid ${statusStyle?.color}`,
                      fontFamily: 'Poppins !important',
                      fontSize: '10px !important',
                      fontStyle: 'normal !important',
                      fontWeight: '400 !important',
                      lineHeight: 'normal !important',
                    }}
                  >
                    <Typography
                      className="w-[6px] h-[6px] rounded-full"
                      style={{ backgroundColor: statusStyle?.color }}
                    ></Typography>
                    <Typography className="status-text">{statusStyle?.value}</Typography>
                  </Box>
                </Box>
              </Box>
              <Box className="flex items-center gap-4">
                <IconButton data-testid="more-options-button" onClick={e => handleMenuClick(e)}>
                  <MoreHorizOutlinedIcon />
                </IconButton>
              </Box>
            </Box>
            {disabledAudit && (
              <ActionAuditBanner
                variant="disabled"
                fields={[
                  {
                    label: 'Disabled by',
                    value: formatActorNameWithRole(disabledAudit.name, disabledAudit.role),
                  },
                  {
                    label: 'Date',
                    value: disabledAudit.at
                      ? formatDateTime(disabledAudit.at, 'DD MMM YYYY, hh:mm:ss A')
                      : '---',
                  },
                  {
                    label: 'Reason',
                    value: formatText(
                      disabledAudit.reason ? disabledAudit.reason : 'No reason mentioned'
                    ),
                  },
                ]}
              />
            )}
            {enabledAudit && (
              <ActionAuditBanner
                variant="enabled"
                fields={[
                  {
                    label: 'Enabled by',
                    value: formatActorNameWithRole(enabledAudit.name, enabledAudit.role),
                  },
                  {
                    label: 'Date',
                    value: enabledAudit.at
                      ? formatDateTime(enabledAudit.at, 'DD MMM YYYY, hh:mm:ss A')
                      : '---',
                  },
                ]}
              />
            )}
            <Box className="flex flex-col items-start gap-[19px] bg-[#FFF] rounded-[8px]">
              <Box sx={{ width: '100%' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                    <Tab
                      label="Overview"
                      aria-label="overview tab"
                      sx={{ textTransform: 'none' }}
                      {...a11yProps(0)}
                    />
                    <Tab
                      label="Sessions"
                      aria-label="sessions tab"
                      sx={{ textTransform: 'none' }}
                      {...a11yProps(1)}
                    />
                    <Tab label="Wallet" sx={{ textTransform: 'none' }} {...a11yProps(2)} />
                    {/*<Tab label="Details" sx={{ textTransform: 'none' }} {...a11yProps(3)} /> */}
                  </Tabs>
                </Box>
                <CustomTabPanel value={value} index={0}>
                  <StudentOverview studentDetails={studentDetails} />
                </CustomTabPanel>
                <CustomTabPanel value={value} index={1}>
                  <StudentSession />
                </CustomTabPanel>
                <CustomTabPanel value={value} index={2}>
                  <StudentWallet studentId={id} />
                </CustomTabPanel>
                {/* <CustomTabPanel value={value} index={3}>
                  <SingleStudentDetails studentDetails={studentDetails} />
                </CustomTabPanel> */}
              </Box>
            </Box>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: { width: '200px', padding: '8px 0' },
            }}
          >
            <MenuItem
              onClick={() => handleAction(accountStatus === 'enabled' ? 'disable' : 'enable')}
            >
              {accountStatus === 'enabled' ? 'Disable Account' : 'Enable Account'}
            </MenuItem>

            {isSupportAssociate && (
              <>
                <Divider sx={{ my: 1, borderColor: '#D5DEE3' }} />
                <MenuItem onClick={handlePasswordReset} disabled={isResettingPassword}>
                  <Typography
                    sx={{
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      fontWeight: 400,
                      lineHeight: '20px',
                      color: '#14171F',
                      opacity: isResettingPassword ? 0.6 : 1,
                    }}
                  >
                    {isResettingPassword ? 'Sending...' : 'Password Reset'}
                  </Typography>
                </MenuItem>
              </>
            )}
          </Menu>
        </Box>
        {openApproveModal && (
          <StudentEnableModal
            setOpenApproveModal={setOpenApproveModal}
            rowId={rowId}
            studentName={studentName}
            isActive={isActive}
            onActionSuccess={getSingleStudentDetails}
          />
        )}
      </Box>
    </Layout>
  );
};

export default StudentDetails;
